import React, { useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
	IOffer,
	IOfferContext,
	OfferContext,
	getFormattedArticlePrice,
	getHelpDrawerCollection,
	getLabelValue,
} from '@adac-mp/shared';
import { Accordion, ContentLoader, Richtext, Typography } from '@adac-mp/components';

interface ICountrySelectionDrawerProps {
	id: string;
}

interface IOfferObject {
	[moduleType: string]: IOffer[];
}

export const CountrySelectionDrawer = ({ id }: ICountrySelectionDrawerProps) => {
	const [searchParams] = useSearchParams();
	const { lang, labels, allOffers, tollSectionOffers } = useContext<IOfferContext>(OfferContext);
	const { loading, data } = useQuery(getHelpDrawerCollection, {
		variables: { lang: lang || 'de', id },
	});

	const drawerContent = data?.helpDrawerCollection.items[0];

	const isSchweizDeactivated = allOffers
		.filter((offer) => offer.supplierId === 14)
		.some((offer) => offer.deactivated);
	const isSchweizAllowed = !isSchweizDeactivated || searchParams.get('enableBAZG') === 'true';

	const allowedSupplierIds = isSchweizAllowed ? [3, 14, 7] : [3, 7];
	const allSuppliers = allowedSupplierIds.filter((suppId) => allOffers.some((offer) => offer.supplierId === suppId));

	const renderVignetteOffersBySupplier = (suppId: number) => {
		const filteredOffers = allOffers.filter(
			(offer) => offer.memberNo && offer.trips === 1 && offer.moduleTypeId === 2 && offer.supplierId === suppId,
		);

		if (!filteredOffers) return null;

		const formattedOffers = Object.entries(
			filteredOffers.reduce((acc: IOfferObject, curr: IOffer) => {
				const { moduleDetail } = curr;
				acc[moduleDetail] = !acc[moduleDetail] ? [curr] : [curr, ...acc[moduleDetail]];
				return acc;
			}, {}),
		).flat(2);

		return (
			<Accordion className="py-2.5 px-1.5 md:py-3" title="Digitale Vignette" chevronColor="#3D3D3D">
				<div className="pb-2 pt-[26px] md:pt-4.5">
					{formattedOffers.map((el, i) =>
						typeof el === 'string' ? (
							<Typography
								key={i}
								size="baseLg"
								color="title"
								variant="abstract"
								component="h4"
								className="py-1.5 px-3.5 [&:not(:first-of-type)]:pt-3.5 [&:not(:first-of-type)]:border-t [&:not(:first-of-type)]:border-b-0 [&:not(:first-of-type)]:border-x-0 [&:not(:first-of-type)]:border-solid [&:not(:first-of-type)]:border-gray-600 [&:not(:first-of-type)]:mt-2">
								{el}
							</Typography>
						) : (
							<Typography
								key={i}
								component="p"
								className="py-1.5 px-3.5 flex items-center justify-between gap-2">
								<Typography size="baseLg" color="title" variant="title">
									{el.serviceCatalogName}
								</Typography>
								<Typography size="baseLg" color="title">
									{getFormattedArticlePrice(el.prices)}
								</Typography>
							</Typography>
						),
					)}
				</div>
			</Accordion>
		);
	};

	const renderUniqueSectionTollOffersBySupplier = (suppId: number) => {
		const filteredOffers = tollSectionOffers.filter(
			({ supplierId, moduleTypeId, trips }) => supplierId === suppId && moduleTypeId === 3 && trips === 1,
		);

		if (!filteredOffers.length) return null;

		return (
			<Accordion className="py-2.5 px-1.5 md:py-3" title="Digitale Streckenmaut" chevronColor="#3D3D3D">
				<div className="pb-2 pt-[26px] md:pt-4.5">
					<Typography size="baseLg" color="title" variant="abstract" component="h4" className="py-1.5 px-3.5">
						{getLabelValue('mp.singleTickets', labels) || 'Einzelfahrtkarten'}
					</Typography>
					{filteredOffers.map((el, i) => (
						<Typography
							key={i}
							component="p"
							className="py-1.5 px-3.5 flex items-center justify-between gap-2">
							<Typography size="baseLg" color="title" variant="title">
								{el.serviceCatalogName} {el.sectionName}
							</Typography>
							<Typography size="baseLg" color="title">
								{getFormattedArticlePrice(el.prices)}
							</Typography>
						</Typography>
					))}
				</div>
			</Accordion>
		);
	};

	const getRichTextHeadlineBySupplierId = (suppId: number) => {
		switch (suppId) {
			case 3:
				return drawerContent.helpDrawerRichText;
			case 7:
				return drawerContent.helpDrawerThirdText;
			case 14:
				return drawerContent.helpDrawerSecondText;
			default:
				return drawerContent.helpDrawerRichText;
		}
	};

	if (loading || !drawerContent)
		return <ContentLoader isLoading hasPortal={false} height="h-[calc(100dvh_-_69px)]" />;

	return (
		<>
			{drawerContent?.headline && (
				<Typography className="font-normal py-6 md:py-8" component="h3" color="title" size="h2">
					{drawerContent?.headline ?? ''}
				</Typography>
			)}
			<div className="flex flex-col gap-4">
				{allSuppliers.map((suppId) => (
					<React.Fragment key={suppId}>
						<div className="px-4 pt-6 mb-4 md:pt-8">
							<Typography color="text" size="xlSpace">
								{drawerContent && drawerContent.helpDrawerRichText ? (
									<Richtext document={getRichTextHeadlineBySupplierId(suppId)} />
								) : (
									''
								)}
							</Typography>
						</div>
						<div className="flex flex-col gap-1.5">
							{renderVignetteOffersBySupplier(suppId)}
							{renderUniqueSectionTollOffersBySupplier(suppId)}
						</div>
					</React.Fragment>
				))}
			</div>
		</>
	);
};
